import { Box, Grid } from "@mui/material";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@platform/api";
import { useOutletContext } from "react-router";
import { Typography } from "@platform/design-system";

import { MonthlyProgressChartCard } from "./components/monthlyProgressChartCard";
import { MonthlyProgressSummaryRow } from "./components/monthlyProgressSummaryRow";
// import { ScenarioMetricColumn } from "./components/scenarioMetricColumn";
import {
  BBEOutletContext,
  ChartData,
  MonthlyProgressApi,
  MonthlyProgressCardsApi,
} from "./types";

const MonthlyProgress = (): React.JSX.Element => {
  const { brokerId } = useOutletContext<BBEOutletContext>();
  const axiosInstance = useApi();

  // Fetch monthly progress data
  const monthlyProgressQuery = useQuery<MonthlyProgressApi[]>({
    queryKey: ["monthlyProgress", brokerId],
    queryFn: () =>
      axiosInstance
        .get<
          MonthlyProgressApi[]
        >(`/bbe/v1/broker/${brokerId}/monthly-progress`)
        .then((res) => res.data),
    enabled: Boolean(brokerId),
  });

  // Fetch monthly progress cards (summary metrics)
  const progressCardsQuery = useQuery<MonthlyProgressCardsApi>({
    queryKey: ["monthlyProgressCards", brokerId],
    queryFn: () =>
      axiosInstance
        .get<MonthlyProgressCardsApi>(
          `/bbe/v1/broker/${brokerId}/monthly-progress-cards`,
        )
        .then((res) => res.data),
    enabled: Boolean(brokerId),
  });

  const goal = progressCardsQuery.data?.goal || 0;
  const payout2024 = progressCardsQuery.data?.prior_year_bonus || 0;

  const monthlyData: ChartData[] = useMemo(() => {
    if (!monthlyProgressQuery.data) return [];

    return monthlyProgressQuery.data.map((item: MonthlyProgressApi) => ({
      name: item.month_name || "",
      guardian: item.guardian_cumulative_bonus || 0,
      lincoln: item.lincoln_cumulative_bonus || 0,
      principal: item.principal_cumulative_bonus || 0,
      goal,
      payout2024,
    }));
  }, [goal, monthlyProgressQuery.data, payout2024]);

  const percentToTarget = progressCardsQuery.data?.percent_to_target || 0;
  const bonusGrowthPct = progressCardsQuery.data?.bonus_growth_percentage || 0;
  const bonusGrowthAmount = progressCardsQuery.data?.bonus_growth_amount || 0;
  const isLoading =
    monthlyProgressQuery.isLoading || progressCardsQuery.isLoading;
  const isError = monthlyProgressQuery.isError || progressCardsQuery.isError;
  const errorMessage =
    monthlyProgressQuery.error?.toString() ||
    progressCardsQuery.error?.toString() ||
    "Unknown error";

  const content = useMemo(() => {
    if (!brokerId) {
      return (
        <Typography color="danger" sx={{ mt: 4 }}>
          Select a broker to view monthly progress data.
        </Typography>
      );
    }

    if (isLoading) {
      return <Typography>Loading...</Typography>;
    }

    if (isError) {
      return (
        <Typography color="danger">
          Error loading data: {errorMessage}
        </Typography>
      );
    }

    return (
      <Grid>
        <Grid size={{ xs: 12, md: 9 }}>
          <MonthlyProgressChartCard title="" data={monthlyData} />
          <MonthlyProgressSummaryRow
            priorYearBonus={payout2024}
            goal={goal}
            percentToTarget={percentToTarget}
            bonusGrowthPct={bonusGrowthPct}
            bonusGrowthAmount={bonusGrowthAmount}
          />
        </Grid>

        {/* <Grid item xs={12} md={3}>
          <ScenarioMetricColumn
            scenariosCreated={75}
            scenarioRecords={1213}
          />
        </Grid> */}
      </Grid>
    );
  }, [
    brokerId,
    bonusGrowthAmount,
    bonusGrowthPct,
    errorMessage,
    goal,
    isError,
    isLoading,
    monthlyData,
    payout2024,
    percentToTarget,
  ]);

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="header-small" sx={{ mb: 3 }} color="textSecondary">
        Monthly Progress
      </Typography>
      {content}
    </Box>
  );
};

export { MonthlyProgress };
